import Device from './Device';

export default class DeviceType {
  static get isTizen() {
    return !!window.tizen;
  }

  static get isLG() {
    // Schnelle, synchrone UA-Erkennung. Die inline-Erkennung in index.html
    // setzt window.isLG / window.lgVersion / window.webos bereits beim
    // HTML-Parsing; hier steht nur ein Fallback, falls externe Scripts die
    // Inline-Erkennung verzoegert haben.
    if (window.isLG === true) return true;
    if (window.isLG === false) return false;
    var ua = (window.navigator && window.navigator.userAgent) || '';
    var detected = !window.tizen && (ua.indexOf("LGE") > 0 || ua.indexOf("WebOS") > 0 || ua.indexOf("webOS") > 0 || ua.indexOf("STB-") > 0);
    window.isLG = detected;
    if (detected) {
      if (!window.webos) window.webos = DeviceType._webOSFromUA(ua);
      if (!window.lgVersion) window.lgVersion = window.webos || (ua.indexOf("STB-5500") > 0 ? '3.5' : '5.0');
    }
    return detected;
  }

  // Chrome-Version -> webOS-Generation (LG-dokumentierte Zuordnung).
  // Hospitality-Modell STB-5500 ist fix webOS 3.5.
  //
  // ACHTUNG: Das ist nur der Fallback. Die Chrome-Schwellen ab webOS 24 sind
  // nicht so hart dokumentiert wie die aelteren, deshalb ist die UA-Ableitung
  // fuer ein Gate ">= 25" allein NICHT ausreichend. Autoritativ sind die
  // Geraete-Properties (siehe refineWebOSVersion); die UA liefert nur einen
  // Startwert, damit vor 'hcap-ready' ueberhaupt ein Wert vorhanden ist.
  static _webOSFromUA(ua) {
    if (!ua) return null;
    if (ua.indexOf("STB-5500") > 0) return '3.5';
    var m = ua.match(/Chrome\/(\d+)/);
    if (!m) return null;
    var c = parseInt(m[1], 10);
    if (c >= 130) return '25';
    if (c >= 120) return '24';
    if (c >= 108) return '23';
    if (c >= 94)  return '22';
    if (c >= 87)  return '6.0';
    if (c >= 79)  return '5.0';
    if (c >= 68)  return '4.5';
    if (c >= 53)  return '4.0';
    if (c >= 38)  return '3.0';
    if (c >= 34)  return '2.0';
    return '1.0';
  }

  // Zerlegt die LG-UA in die Teile, die fuer die Diagnose zaehlen.
  //
  // Beispiel (Hospitality, Pro:Centric-Browser):
  //   Mozilla/5.0 (Web0S; Linux/SmartTV) AppleWebKit/537.36 (KHTML, like Gecko)
  //   PCBR/120.0.6099.270 Chrome/120.0.6099.270 Safari/537.36 PBRM/3.0
  //   ( ;LGE ;43UK660H0LA.BEUFLJP ;03.62.00 ;1 )
  //
  // WICHTIG: Ist PCBR vorhanden, stammt die Chrome-Version vom Pro:Centric-
  // Browser und NICHT von der webOS-Plattform - LG pflegt den PCBR unabhaengig
  // von der Geraetegeneration. Ein 43UK660H mit webOS 25 meldet Chrome 120, was
  // die Chrome-Tabelle auf webOS 24 abbilden wuerde. Die Ableitung ist dort
  // also nachweislich falsch und darf kein Feature-Gate bestimmen.
  static _uaDetails(ua) {
    var out = { pcbr: null, chrome: null, model: null, firmware: null };
    if (!ua) return out;
    var m = ua.match(/PCBR\/([\d.]+)/i);
    if (m) out.pcbr = m[1];
    m = ua.match(/Chrome\/([\d.]+)/);
    if (m) out.chrome = m[1];
    // "( ;LGE ;<modell> ;<firmware> ;<n> )"
    m = ua.match(/;\s*LGE\s*;\s*([^;]+?)\s*;\s*([^;]+?)\s*;/i);
    if (m) {
      out.model = m[1];
      out.firmware = m[2];
    }
    return out;
  }

  // webOS-Generation aus dem LG-Modellcode - ausschliesslich anhand konkret
  // vermessener Geraete.
  //
  // Hintergrund: auf Hospitality-Firmware liefert KEINE hcap-Property die
  // Generation. platform_version gibt die Firmware ("3.62.00"),
  // hcap_middleware_version die HCAP-Spec-Version ("1.24.0.817"), und die
  // Chrome-Version stammt vom Pro:Centric-Browser (PCBR). Der Modellcode ist
  // damit die einzige verbleibende Quelle.
  //
  // WICHTIG - hier war schon ein Fehler drin: LGs CONSUMER-Namensschema kodiert
  // das Modelljahr in den zwei Buchstaben nach der Zollangabe (UK = 2018 usw.).
  // Fuer die HOSPITALITY-Linie gilt das NICHT: der 43UK660H ist ein Geraet von
  // 2025 mit webOS 25. Eine aus der Buchstabenfolge abgeleitete Heuristik
  // liefert dort grob falsche Ergebnisse und wuerde Cast auf einem webOS-25-
  // Geraet abschalten. Deshalb steht hier KEINE Heuristik mehr, sondern nur
  // eine Tabelle vermessener Serien. Unbekannte Serien liefern null - dann
  // entscheidet die Capability-Erkennung (siehe helpers/Cast/LG.js), die ohne
  // Versionsangabe funktioniert.
  //
  // Beim Vermessen eines neuen Geraets: Serie und Generation von der Info-Seite
  // ablesen und hier ergaenzen.
  static _webOSFromModel(model) {
    if (!model) return null;
    // "43UK660H0LA.BEUFLJP" -> "UK660H"
    var m = String(model).toUpperCase().match(/^(?:OLED)?\d{2,3}([A-Z]{2}\d{3}[A-Z])/);
    if (!m) return null;
    var measured = {
      UK660H: '25', // 43UK660H0LA, Firmware 03.62.00, PCBR 120 - webOS 25
    };
    return measured[m[1]] || null;
  }

  // Explizite webOS-Angabe im UA, falls die Firmware sie mitschickt. Das ist
  // genauer als die Chrome-Ableitung. Bekannte Formen:
  //   "Web0S/25.0.0" (mit Null!), "webOS.TV-2025", "WEBOS25"
  static _webOSFromUAExplicit(ua) {
    if (!ua) return null;
    var m = ua.match(/Web0S[/ ]v?(\d+(?:\.\d+)*)/i);
    if (m) return DeviceType._normalizeWebOS(m[1]);
    m = ua.match(/webOS\.TV-(\d{4})/i);
    if (m) return DeviceType._normalizeWebOS(m[1]);
    m = ua.match(/WEBOS(\d{2})(?!\d)/i);
    if (m) return DeviceType._normalizeWebOS(m[1]);
    return null;
  }

  // Normalisiert eine beliebige LG-Versionsangabe auf die Generation, die in
  // Doku und Feature-Gates benutzt wird ("3.5", "4.5", "5.0", "6.0", "22".."26").
  //
  // LG zaehlt die Plattform intern weiter (7.x, 8.x, 9.x, 10.x), benennt die
  // Geraetegeneration aber nach Jahr (webOS 22, 23, 24, 25). Die Zuordnung ab
  // 7.x ist aus der Versionsfolge abgeleitet und muss auf echter Hardware
  // geprueft werden - die Info-Seite zeigt dafuer immer auch den Rohwert an.
  // Gibt null zurueck, wenn der Wert plausibel KEINE webOS-Generation ist.
  // Das ist der wichtige Teil: die Property "platform_version" liefert auf
  // Hospitality-Firmware die FIRMWARE-Version - auf einem 43UK660H mit webOS 25
  // steht dort "3.62.00". Ohne Plausibilitaetspruefung wuerde daraus still
  // "webOS 3.0", also eine um 22 Generationen falsche Angabe, auf der dann
  // Feature-Gates aufsetzen. Echte webOS-Versionen sind 1.x-6.x mit Minor 0
  // oder 5, oder die Generationen 22..26.
  static _normalizeWebOS(value) {
    if (value === null || value === undefined || value === '') return null;
    var s = String(value).trim();
    var parts = s.match(/^(\d+)(?:\.(\d+))?/);
    if (!parts) return null;
    var major = parseInt(parts[1], 10);
    if (isNaN(major)) return null;
    var minor = parts[2] === undefined ? 0 : parseInt(parts[2], 10);

    // Jahresangabe ("2025") -> Generation ("25").
    if (major >= 2000 && major < 2100) return String(major - 2000);
    // Generationsangabe. Alles jenseits von 30 ist keine.
    if (major >= 22) return major <= 30 ? String(major) : null;
    // Interne Plattformversion -> Generation.
    var generations = { 7: '22', 8: '23', 9: '24', 10: '25', 11: '26' };
    if (generations[major]) return generations[major];
    // Alte Generationen: 1.x bis 6.x, Minor immer 0 oder 5. Ein Minor daneben
    // (Firmware "3.62.00", hcap "1.24.12") ist keine webOS-Version.
    if (major < 1 || major > 6) return null;
    if (minor !== 0 && minor !== 5) return null;
    return major + '.' + minor;
  }

  // Liefert die WebOS-Version synchron aus window.webos oder UA-Fallback.
  static get getWebOSVersion() {
    if (window.webos) return window.webos;
    if (!this.isLG) return null;
    var ua = (window.navigator && window.navigator.userAgent) || '';
    window.webos = DeviceType._webOSFromUAExplicit(ua) || DeviceType._webOSFromUA(ua);
    return window.webos;
  }

  // Numerische Generation fuer Vergleiche (z.B. Cast-Gate ">= 25").
  // Alte Geraete liefern 3.5/4.5/5/6, neue 22/23/24/25 - ein einfacher
  // parseFloat-Vergleich ist dadurch in beide Richtungen eindeutig.
  static get getWebOSMajor() {
    var v = parseFloat(DeviceType.getWebOSVersion);
    return isNaN(v) ? 0 : v;
  }

  // Liest eine hcap-Property als Promise. Bewusst lokal (statt ueber
  // helpers/Device/LG), damit DeviceType keine Zyklen aufbaut.
  // resolve(null) statt reject, damit Promise.all nie abbricht.
  static _hcapProperty(key) {
    return new Promise(function(resolve) {
      try {
        if (typeof hcap === 'undefined' || !hcap || !hcap.property) {
          resolve(null);
          return;
        }
        var done = false;
        var finish = function(v) {
          if (done) return;
          done = true;
          resolve(v);
        };
        // Manche Firmwares rufen bei unbekannten Keys keinen Callback -> Timeout.
        setTimeout(function() { finish(null); }, 3000);
        hcap.property.getProperty({
          key: key,
          onSuccess: function(s) {
            finish(s && typeof s.value !== 'undefined' ? s.value : null);
          },
          onFailure: function() { finish(null); },
        });
      } catch (e) {
        resolve(null);
      }
    });
  }

  // Verfeinert window.webos mittels der Geraete-APIs, sobald diese bereit sind,
  // und legt alle Rohwerte in window.webosInfo ab (Grundlage der Info-Seite).
  //
  // Quellen, absteigend nach Verlaesslichkeit:
  //  1. hcap-Property "platform_version"        -> Plattformversion des TV
  //  2. hcap-Property "hcap_middleware_version" -> HCAP-Version DES GERAETS
  //  3. webOS.deviceInfo({sdkVersion, ...})     -> nur Consumer-Firmware
  //  4. UA (explizit, dann Chrome-Ableitung)
  //
  // WICHTIG: hcap.API_VERSION ist eine Konstante in der mitgelieferten
  // static/hcap.js und beschreibt die App, NICHT den Fernseher. Sie darf
  // deshalb nicht zum Gaten von Features benutzt werden - dafuer ist
  // hcap_middleware_version zustaendig.
  static refineWebOSVersion(force) {
    if (DeviceType._refinePromise && !force) return DeviceType._refinePromise;

    var ua = DeviceType.getUserAgent;
    var info = window.webosInfo || (window.webosInfo = {});
    info.userAgent = ua;
    var details = DeviceType._uaDetails(ua);
    info.chromeVersion = details.chrome;
    info.pcbrVersion = details.pcbr;
    info.uaModel = details.model;
    info.uaFirmware = details.firmware;
    // Auf Pro:Centric-Browsern ist die Chrome-Version keine Aussage ueber die
    // webOS-Generation (siehe _uaDetails).
    info.uaChromeUnreliable = !!details.pcbr;
    info.fromUaExplicit = DeviceType._webOSFromUAExplicit(ua);
    info.fromUaChrome = DeviceType._webOSFromUA(ua);
    info.hcapJsVersion = null;
    try {
      if (typeof hcap !== 'undefined' && hcap && hcap.API_VERSION) {
        info.hcapJsVersion = String(hcap.API_VERSION);
        window.hcapApiVersion = info.hcapJsVersion;
      }
    } catch (e) { /* hcap nicht geladen */ }

    var deviceInfo = new Promise(function(resolve) {
      try {
        if (typeof webOS === 'undefined' || !webOS || typeof webOS.deviceInfo !== 'function') {
          resolve(null);
          return;
        }
        var done = false;
        setTimeout(function() { if (!done) { done = true; resolve(null); } }, 3000);
        webOS.deviceInfo(function(i) {
          if (done) return;
          done = true;
          resolve(i || null);
        });
      } catch (e) {
        resolve(null);
      }
    });

    DeviceType._refinePromise = Promise.all([
      DeviceType._hcapProperty('platform_version'),
      DeviceType._hcapProperty('hcap_middleware_version'),
      DeviceType._hcapProperty('model_name'),
      deviceInfo,
    ]).then(function(res) {
      info.platformVersion = res[0];
      info.hcapMiddlewareVersion = res[1];
      info.modelName = res[2];
      info.deviceInfo = res[3];
      if (res[3]) {
        window.webosDeviceInfo = res[3];
        info.sdkVersion = res[3].sdkVersion || res[3].version || null;
      }

      info.fromPlatformVersion = DeviceType._normalizeWebOS(info.platformVersion);
      info.fromSdkVersion = DeviceType._normalizeWebOS(info.sdkVersion);
      // Modellcode bevorzugt aus der Property, sonst aus der UserAgent.
      info.modelCode = info.modelName || info.uaModel || null;
      info.fromModel = DeviceType._webOSFromModel(info.modelCode);

      // Reihenfolge nach Verlaesslichkeit. Der Modellcode steht VOR der
      // Chrome-Ableitung: er ist deterministisch, waehrend die Chrome-Version
      // auf Pro:Centric-Browsern nachweislich falsche Generationen ergibt.
      var resolved =
        info.fromPlatformVersion ||
        info.fromSdkVersion ||
        info.fromUaExplicit ||
        info.fromModel ||
        info.fromUaChrome ||
        null;
      // Bewusst NUR window.webos: window.lgVersion wird in den Player-Seiten
      // (TVPlayer/SourceSelect/RadioPlayer/InputSource) per String-Vergleich
      // gegen '5.0' ausgewertet. Ein nachtraeglich geaenderter Wert wuerde dort
      // still in einen anderen Template-Zweig kippen.
      if (resolved) window.webos = resolved;
      info.resolved = window.webos || null;
      info.source = info.fromPlatformVersion
        ? 'platform_version'
        : info.fromSdkVersion
        ? 'webOS.deviceInfo'
        : info.fromUaExplicit
        ? 'userAgent'
        : info.fromModel
        ? 'Modellcode (vermessen)'
        : info.uaChromeUnreliable
        ? 'userAgent(chrome via PCBR - UNZUVERLAESSIG)'
        : 'userAgent(chrome)';
      // Nur die Chrome-Ableitung uebrig UND PCBR im Spiel -> der Wert ist
      // wertlos. Sichtbar machen statt still ein Feature-Gate darauf stuetzen.
      info.unreliable = !info.fromPlatformVersion && !info.fromSdkVersion &&
        !info.fromUaExplicit && !info.fromModel && !!info.uaChromeUnreliable;
      if (info.unreliable) {
        console.log(
          '[DeviceType] WebOS-Version unzuverlaessig: nur Chrome-Ableitung aus ' +
          'einer PCBR-UserAgent (' + info.chromeVersion + '), Modell ' + info.uaModel
        );
      }
      return info.resolved;
    }).catch(function(e) {
      console.log('refineWebOSVersion error', e);
      return window.webos || null;
    });

    return DeviceType._refinePromise;
  }

  // Rohwerte der Versionserkennung (fuer die Info-/Diagnose-Seite).
  static get getWebOSInfo() {
    return window.webosInfo || null;
  }

  // true, wenn die ermittelte Generation nur aus der Chrome-Version einer
  // PCBR-UserAgent stammt. Ein solcher Wert darf kein Feature FREISCHALTEN -
  // sonst wird Cast auf 2018er Geraeten angeboten, die Chrome 120 melden.
  // Vor refineWebOSVersion() ist das Ergebnis noch unbekannt -> false.
  static get isWebOSVersionUnreliable() {
    var info = window.webosInfo;
    return !!(info && info.unreliable);
  }
  static get isOrsay() {
    if(window.isOrsay === true) return true;
    if(window.isOrsay === false) return false;
    return !!window.orsay;
  }
  static get isRPI() {
    // let socketio = io('http://127.0.0.1:4000/');
    // if (socketio)
    if (window.io) return true;
    return false;
  }
  static get isBrowser() {
    // this.isOrsay - ohne das this warf isBrowser einen ReferenceError,
    // sobald die Auswertung ueberhaupt bis hierher kam (auf einem TV bricht die
    // Kette vorher ab, im Desktop-Browser nicht).
    return !this.isTizen && !this.isLG && !this.isRPI && !this.isOrsay;
  }
  static get getDeviceType() {
    if (this.isTizen) return 'tv-tizen';
    if (this.isOrsay) return 'tv-orsay';
    if (this.isLG) return 'tv-lge';
    if (this.isRPI) return 'tv-rpi';
    return 'tv-default';
  }

  static get getUserAgent() {
    if (!window.navigator) return '';
    return window.navigator.userAgent;
  }

  static get getDeviceModel() {
    return Device.model;
  }

  static get getDeviceSoftware() {
    return Device.software;
  }

  static get getMAC() {
    //return '00:00:00:00:00:00';
    return Device.mac;
  }

  static get getIP() {
    //return '0.0.0.0';
    return Device.ip;
  }

  static get getSerial() {
    //console.log("======================================================getSERIAL_0", webapis.systemcontrol.getSerialNumber());
    return Device.serialNumber;
  }

  static get getResHeight() {
    return Device.resolutionHeight;
  }

  static get getResWidth() {
    return Device.resolutionWidth;
  }

  static async getDefaultDevice() {
    const [model, version, software, mac, ip, serialNumber, platform, resHeight, resWidth] = await Promise.all([
      Device.model,
      Device.version,
      Device.firmware,
      Device.mac,
      Device.ip,
      Device.serialNumber,
      Device.platform,
      Device.resolutionHeight,
      Device.resolutionWidth,
    ]);

    const device = {
      buildingId: null,
      groupId: null,
      type: this.getDeviceType,
      agent: this.getUserAgent,
      updated: Math.floor(new Date().getTime() / 1000),
      model,
      version,
      software,
      mac,
      ip,
      serialNumber,
      platform,
      resHeight,
      resWidth,
    };
    console.log('=====================================getDefaultDevice', device);
    return device;
  }
}



// WEBPACK FOOTER //
// ./src/helpers/DeviceType.js